import { GridRowSelectionModel } from "@mui/x-data-grid-premium";
import { useTenantData } from "contexts/tenant";
import { useCallback, useMemo, useState } from "react";
import { HeatQueueEntry } from "src/store/api/generatedApi";
import {
  resolveContiguousSelection,
  selectableStableHeatIds,
} from "./selection";

export const useContiguousHeatSelection = (heats: HeatQueueEntry[]) => {
  const { heat_override_max_heats: maxHeats } = useTenantData();
  const [selectedStableHeatIds, setSelectedStableHeatIds] = useState<string[]>(
    []
  );

  const eligibleStableHeatIds = useMemo(
    () =>
      new Set(
        heats
          .filter((heat) => heat.override_id === null)
          .map((heat) => heat.stable_heat_id)
      ),
    [heats]
  );

  const applySelection = useCallback(
    (candidateStableHeatIds: string[]): string[] =>
      resolveContiguousSelection({
        heats,
        candidateStableHeatIds,
        eligibleStableHeatIds,
        maxHeats,
      }),
    [heats, eligibleStableHeatIds, maxHeats]
  );

  const resolvedSelectedStableHeatIds = applySelection(selectedStableHeatIds);
  if (
    resolvedSelectedStableHeatIds.length !== selectedStableHeatIds.length ||
    resolvedSelectedStableHeatIds.some(
      (stableHeatId, index) => stableHeatId !== selectedStableHeatIds[index]
    )
  ) {
    setSelectedStableHeatIds(resolvedSelectedStableHeatIds);
  }

  const selectedHeats = useMemo(
    () =>
      heats.filter((heat) =>
        selectedStableHeatIds.includes(heat.stable_heat_id)
      ),
    [heats, selectedStableHeatIds]
  );

  const selectableIds = useMemo(
    () =>
      selectableStableHeatIds({
        heats,
        selectedStableHeatIds,
        eligibleStableHeatIds,
        maxHeats,
      }),
    [heats, selectedStableHeatIds, eligibleStableHeatIds, maxHeats]
  );

  const toggleHeat = useCallback(
    (stableHeatId: string) => {
      const next = selectedStableHeatIds.includes(stableHeatId)
        ? selectedStableHeatIds.filter((id) => id !== stableHeatId)
        : [...selectedStableHeatIds, stableHeatId];
      setSelectedStableHeatIds(applySelection(next));
    },
    [selectedStableHeatIds, applySelection]
  );

  const handleSelectionModelChange = useCallback(
    (model: GridRowSelectionModel) => {
      const resolved = heats
        .filter((heat) =>
          model.type === "include"
            ? model.ids.has(heat.heat_id)
            : !model.ids.has(heat.heat_id)
        )
        .map((heat) => heat.stable_heat_id);
      setSelectedStableHeatIds(applySelection(resolved));
    },
    [heats, applySelection]
  );

  const clearSelection = useCallback(() => {
    setSelectedStableHeatIds([]);
  }, []);

  return {
    selectedHeats,
    selectableIds,
    toggleHeat,
    handleSelectionModelChange,
    clearSelection,
  };
};
